import { storeToRefs } from 'pinia'
import { Router } from 'vue-router'
/**
 * Open router in modal.
 * This guard helps to open router in modal by intercepting route changes.
 *
 * We do this operation in the beforeEach hook to handle any route that should
 * be opened in a modal/drawer before the actual navigation happens.
 *
 * @param router Vue Router instance
 */
export function createDrawerGuard(router: Router) {
  router.beforeEach(async (to) => {
    const drawerStore = useRouterDrawerStore()
    const {
      nextRoute,
      middleViews,
      backgroundView,
      closeDrawer,
      setNextRoute,
      setLastComponent,
      setBackgroundView,
      closeRouteInDrawer,
    } = drawerStore

    const { isNavigating } = storeToRefs(drawerStore)

    // TODO: This check prevents multiple navigations from happening simultaneously
    // Currently commented out as it affects normal navigation flow
    // if (isNavigating.value) return false

    isNavigating.value = true

    /**
     * Handle navigation when there are middle views (drawer views)
     * This section checks if we're navigating back through drawer views:
     *
     * 1. Check if there are any middle views in the stack
     * 2. Compare the last middle view with target route
     * 3. If they match:
     *    - Remove the view from middleViews array (pop)
     *    - Set it as the next route and last component
     *    - Allow navigation to proceed
     *
     * This enables proper back navigation through drawer history
     */
    if (middleViews.length > 0) {
      const lastView = middleViews[middleViews.length - 1]
      if (lastView.fullPath === to.fullPath) {
        middleViews.pop()
        setNextRoute(lastView)
        setLastComponent(lastView)
        return true
      }
    }

    /**
     * Handle background view cleanup
     * When user navigates to a different page (not in drawer):
     * - Clear the background view if there's no next route
     * - And if target route is different from current background view
     *
     * Example: Navigating from index page to issues page
     */
    if (!nextRoute && to.fullPath !== backgroundView) {
      setBackgroundView(undefined)
    }

    /**
     * Handle drawer closure on navigation
     * When there's a next route and we're navigating to a different route:
     *
     * 1. If target route is not the background view:
     *    - Close the entire drawer
     *    - Clear background view
     *
     * 2. If target route is the background view:
     *    - Only close the current route in drawer
     *    Example: Going back from issues page to index page
     */
    if (nextRoute && nextRoute?.name !== to.name) {
      if (to.fullPath !== backgroundView) {
        closeDrawer()
        setBackgroundView(undefined)
      } else {
        closeRouteInDrawer(-1)
      }
    }
    return true
  })

  /**
   * Reset navigation state after route change completes
   * This ensures the navigation flag is reset after each navigation
   * to prevent any stale states
   */
  router.afterEach(() => {
    const drawerStore = useRouterDrawerStore()
    const { isNavigating } = storeToRefs(drawerStore)
    isNavigating.value = false
  })
}
